import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  Container,
  Card,
  Row,
  Col,
  Image,
  Form,
  Button,
  FloatingLabel,
  Spinner,
} from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";

import apiService from "../../services/apiService";
import { useAuth } from "../../contexts/authContext";
import { useNotification } from "../../components/notification";

import { cartSession } from "../../utils/cart";

import logo from "../../logo.png";

const Login = () => {
  // Separate form hooks for phone and email
  const phoneForm = useForm();
  const emailForm = useForm();

  const navigate = useNavigate();
  const location = useLocation();

  const { login } = useAuth();
  const { showNotification } = useNotification();

  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [isPhoneLoading, setIsPhoneLoading] = useState(false);

  const onEmailSubmit = async (data) => {
    try {
      setIsEmailLoading(true);
      const response = await apiService.post("/auth/login", data);
      const { token, refreshToken, user } = response;

      if (user.emailVerified) {
        login({ token, refreshToken, user });

        const isSessionExists = cartSession.isExist();
        if (isSessionExists) {
          syncCart();
        }
        const redirectTo = location.state?.from || "/orders";
        navigate(redirectTo);
      } else navigate("/confirm-email", { state: { email: data.email } });
    } catch (error) {
      showNotification("danger", error.message || "Login failed!", 5000);
    } finally {
      setIsEmailLoading(false);
    }
  };

  const onPhoneSubmit = async (data) => {
    try {
      setIsPhoneLoading(true);
      await apiService.post("/otp/send-phoneOTP", {
        phone: data.phone,
        isLogin: true,
      });

      showNotification(
        "success",
        "OTP sent to your registered phone successfully!",
        3000
      );

      // Redirect to OTP entry page
      navigate("/login-with-otp", {
        state: {
          phone: data.phone,
          from: location.state?.from || "/orders",
        },
      });
    } catch (error) {
      console.log(error);
      showNotification("danger", error || "Failed to send OTP!", 5000);
    } finally {
      setIsPhoneLoading(false);
    }
  };

  useEffect(() => {
    if (location.state?.phone) {
      phoneForm.setValue("phone", location.state.phone);
    }
  }, [location.state, phoneForm]);

  const syncCart = async () => {
    try {
      await apiService.post("/cart/merge", {
        sessionId: cartSession.get(),
      });
    } catch (error) {
      console.error(
        error.message || "Failed to sync cart with authenticated user."
      );
    }
  };

  return (
    <Container className="min-vh-100 d-flex align-items-center justify-content-center flex-column">
      <Row>
        <Col xs={12} className="text-center mb-4">
          <Link to="/">
            <Image
              src={logo}
              alt={`${process.env.REACT_APP_PORTAL}`}
              fluid
              width="225px"
              style={{ cursor: "pointer" }}
            />
          </Link>
        </Col>
        <Col xs={11} md={8} lg={6} xl={6} xxl={6} className="m-auto">
          <Card className="p-4 shadow border-0">
            <Card.Body>
              <div className="mb-2">
                <h5 className="text-center mb-3">Sign In</h5>
                <Form onSubmit={phoneForm.handleSubmit(onPhoneSubmit)}>
                  <FloatingLabel
                    controlId="txtPhone"
                    label="Phone Number"
                    className="mb-3"
                  >
                    <Form.Control
                      type="text"
                      placeholder="Phone Number"
                      isInvalid={!!phoneForm.formState.errors.phone}
                      {...phoneForm.register("phone", {
                        required: "Phone number is required",
                        pattern: {
                          value: /^[0-9]{10}$/,
                          message: "Invalid 10-digit phone number",
                        },
                      })}
                    />
                    {phoneForm.formState.errors.phone && (
                      <Form.Text className="text-danger">
                        {phoneForm.formState.errors.phone.message}
                      </Form.Text>
                    )}
                  </FloatingLabel>

                  <div className="d-grid gap-2">
                    <Button
                      variant="primary"
                      disabled={isPhoneLoading}
                      type="submit"
                      className="btn-main"
                    >
                      {isPhoneLoading ? (
                        <Spinner animation="border" role="status" size="sm" />
                      ) : (
                        "Login with OTP"
                      )}
                    </Button>
                  </div>
                </Form>
              </div>

              {/* Divider */}
              <div className="position-relative my-4">
                <hr className="my-4" />
                <div className="position-absolute top-50 start-50 translate-middle bg-white px-3 text-muted">
                  OR
                </div>
              </div>

              {/* Email Login Form */}
              <div className="mt-4">
                <Form onSubmit={emailForm.handleSubmit(onEmailSubmit)}>
                  <FloatingLabel
                    controlId="txtEmail"
                    label="Email"
                    className="mb-3"
                  >
                    <Form.Control
                      type="text"
                      placeholder="Email"
                      isInvalid={!!emailForm.formState.errors.email}
                      {...emailForm.register("email", {
                        required: "Email is required",
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: "Invalid email address",
                        },
                      })}
                    />
                    {emailForm.formState.errors.email && (
                      <Form.Text className="text-danger">
                        {emailForm.formState.errors.email.message}
                      </Form.Text>
                    )}
                  </FloatingLabel>

                  <FloatingLabel
                    controlId="txtPassword"
                    label="Password"
                    className="mb-1"
                  >
                    <Form.Control
                      type="password"
                      placeholder="Enter password"
                      isInvalid={!!emailForm.formState.errors.password}
                      {...emailForm.register("password", {
                        required: "Password is required",
                        minLength: {
                          value: 6,
                          message: "Password must be at least 6 characters",
                        },
                      })}
                    />
                    {emailForm.formState.errors.password && (
                      <Form.Text className="text-danger">
                        {emailForm.formState.errors.password.message}
                      </Form.Text>
                    )}
                  </FloatingLabel>
                  <div className="text-end mb-3">
                    <Link
                      to="/recover-password"
                      title="Recover Password"
                      className="text-decoration-none link-secondary"
                    >
                      Forgot Password?
                    </Link>
                  </div>
                  <div className="d-grid gap-2">
                    <Button
                      variant="primary"
                      disabled={isEmailLoading}
                      type="submit"
                      className="btn-main"
                    >
                      {isEmailLoading ? (
                        <Spinner animation="border" role="status" size="sm">
                          <span className="visually-hidden">Loading...</span>
                        </Spinner>
                      ) : (
                        "LOGIN"
                      )}
                    </Button>
                  </div>
                </Form>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} className="text-center mt-3">
          <div className="text-dark">
            Don't have an account?{" "}
            <Link
              to="/register"
              title="Register Now"
              className="text-decoration-none link-secondary fw-semibold"
            >
              Register Now
            </Link>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default Login;
